import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Search } from "lucide-react";
import dynamicIconImports from "lucide-react/dynamicIconImports";

const IconSelect = ({
  label,
  value,
  onChange,
  error,
  placeholder = "Select an icon...",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [iconComponents, setIconComponents] = useState({});
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Get all icon names from dynamicIconImports
  const iconNames = Object.keys(dynamicIconImports);

  // Filter icons based on search term
  const filteredIcons = searchTerm
    ? iconNames.filter((iconName) =>
        iconName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : iconNames.slice(0, 100); // Show first 100 icons for performance

  // Dynamically import icon components as needed
  useEffect(() => {
    const loadIcons = async () => {
      const iconsToLoad = filteredIcons.slice(0, 50); // Load first 50 visible icons
      const newIconComponents = { ...iconComponents };

      for (const iconName of iconsToLoad) {
        if (!newIconComponents[iconName]) {
          try {
            const iconModule = await dynamicIconImports[iconName]();
            newIconComponents[iconName] = iconModule.default;
          } catch (error) {
            console.warn(`Failed to load icon: ${iconName}`, error);
          }
        }
      }

      setIconComponents(newIconComponents);
    };

    if (isOpen && filteredIcons.length > 0) {
      loadIcons();
    }
  }, [isOpen, filteredIcons.join(",")]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (iconName) => {
    onChange(iconName);
    setIsOpen(false);
    setSearchTerm("");
  };

  const renderIcon = (iconName) => {
    const IconComponent = iconComponents[iconName];
    if (IconComponent) {
      return <IconComponent size={18} />;
    }
    return (
      <div className="w-[18px] h-[18px] bg-gray-200 animate-pulse rounded"></div>
    );
  };

  // For selected icon, load it if not already loaded
  useEffect(() => {
    const loadSelectedIcon = async () => {
      if (value && !iconComponents[value]) {
        try {
          const iconModule = await dynamicIconImports[value]();
          setIconComponents((prev) => ({
            ...prev,
            [value]: iconModule.default,
          }));
        } catch (error) {
          console.warn(`Failed to load selected icon: ${value}`, error);
        }
      }
    };

    if (value) {
      loadSelectedIcon();
    }
  }, [value]);

  const selectedIconName = value || "";
  const SelectedIcon = selectedIconName
    ? iconComponents[selectedIconName]
    : null;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <label className="block font-bold title text-warmgray-900 mb-1">
        {label}
      </label>

      {/* Selected Value Display */}
      <div
        className={`flex items-center justify-between w-full px-3 py-3 border rounded-md cursor-pointer ${
          error
            ? "border-red-500"
            : isOpen
            ? "border-violet-400"
            : "border-warmgray-300"
        } bg-white hover:border-violet-400 transition-colors`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          {SelectedIcon && <SelectedIcon size={20} />}
          <span
            className={selectedIconName ? "text-gray-900" : "text-gray-500"}
          >
            {selectedIconName || placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          className={`transform transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search icons..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-violet-400"
              />
            </div>
          </div>

          {/* Icons Grid */}
          <div className="overflow-y-auto max-h-64">
            {filteredIcons.length > 0 ? (
              <div className="grid grid-cols-6 gap-1 p-2">
                {filteredIcons.slice(0, 50).map((iconName) => (
                  <div
                    key={iconName}
                    className={`flex flex-col items-center p-2 min-h-[50px] rounded cursor-pointer hover:bg-gray-100 transition-colors ${
                      value === iconName ? "bg-violet-100 text-violet-600" : ""
                    }`}
                    onClick={() => handleSelect(iconName)}
                    title={iconName}
                  >
                    <div className="flex-shrink-0 mb-1">
                      {renderIcon(iconName)}
                    </div>
                    <span className="text-[9px] text-center leading-tight break-all">
                      {iconName}
                    </span>
                  </div>
                ))}
              </div>
            ) : searchTerm ? (
              <div className="p-4 text-center text-gray-500">
                No icons found matching "{searchTerm}"
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500">
                Loading icons...
              </div>
            )}
            {!searchTerm && iconNames.length > 100 && (
              <div className="p-2 text-center text-xs text-gray-500">
                Showing first 100 icons. Use search to find more from{" "}
                {iconNames.length} total icons.
              </div>
            )}
            {searchTerm && filteredIcons.length > 50 && (
              <div className="p-2 text-center text-xs text-gray-500">
                Showing first 50 results. Be more specific to see all matches.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <span className="block text-red-500 mt-1">
          <small>{error}</small>
        </span>
      )}
    </div>
  );
};

export default IconSelect;
